import type { UnsubscribeRequest } from "@/modules/events/types/eventDetailsApi"
import type { FunctionComponent } from "react"

import { isAfter } from "date-fns"
import { useParams } from "next/navigation"
import { useMemo, useState } from "react"

import { Button } from "@jobteaser/spark/components/Button"
import { Text } from "@jobteaser/spark/components/Text"

import { JOBTEASER } from "@/modules/events/constants"
import { useEventDetailsData } from "@/modules/events/hooks/useEventDetailsData"
import { useRegistrationStatus } from "@/modules/events/hooks/useRegistrationStatus"
import { useUnsubscribe } from "@/modules/events/hooks/useUnsubscribe"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { Modal } from "@/modules/layouts/components/Modal/Modal"
import { useLocale } from "@/modules/locales/useLocale"
import { useNotification } from "@/modules/notification/hooks"

import styles from "./Unsubscribe.module.css"

type UnsubscribeProps = {
  id: string
}

export const Unsubscribe: FunctionComponent<UnsubscribeProps> = ({ id }) => {
  const { t } = useTranslation(["event_details", "shared_modal", "shared_error"])
  const locale = useLocale()
  const showNotification = useNotification()
  const { refetch: refetchRegistrationStatus } = useRegistrationStatus({ eventId: id, locale })
  const hostname = typeof window !== "undefined" ? window.location.host : ""
  const { eventSlug } = useParams<{ eventSlug: string }>()
  const event = useEventDetailsData(eventSlug)

  const params = useMemo(
    () =>
      ({
        hostname,
        id,
        locale,
      }) as UnsubscribeRequest,
    [id, locale, hostname]
  )

  const { mutate: getUnsubscribe } = useUnsubscribe()
  const [isUnsubscribeModalOpen, setIsUnsubscribeModalOpen] = useState(false)
  const [isButtonLoading, setIsButtonLoading] = useState(false)

  if (typeof event === "undefined") {
    return null
  }

  const { registrationDetails } = event

  if (registrationDetails.type !== JOBTEASER) {
    return null
  }

  const currentDate = new Date()
  const unregistrationDate = new Date(registrationDetails.unregistrationDeadline)
  const isUnregistrationDeadlineExpired = isAfter(currentDate, unregistrationDate)

  if (isUnregistrationDeadlineExpired) {
    return null
  }
  const handleConfirm = (): void => {
    // submit request then close modal
    setIsButtonLoading(true)
    getUnsubscribe(params, {
      onError: () => {
        showNotification({
          title: t("shared_error.generic"),
          variant: "error",
        })
        setIsButtonLoading(false)
        setIsUnsubscribeModalOpen(false)
      },
      onSuccess: () => {
        refetchRegistrationStatus()
        showNotification({
          title: t("event_details.notifications.unsubscribe.success.title"),
          variant: "success",
        })
        setIsButtonLoading(false)
        setIsUnsubscribeModalOpen(false)
      },
    })
  }

  return (
    <>
      <Button
        className={styles.link}
        onClick={() => setIsUnsubscribeModalOpen(true)}
        data-testid="event-details-registration-details-unsubscribe-link"
        variant="tertiary"
      >
        {t("event_details.unsubscribe")}
      </Button>

      {isUnsubscribeModalOpen && (
        <Modal
          closeLabel={t("shared_modal.close")}
          data-testid="event-details-registration-details-unsubscribe-modal"
          onClose={() => setIsUnsubscribeModalOpen(false)}
          footer={
            <Button
              data-testid="event-details-registration-details-unsubscribe-modal-confirm-button"
              isLoading={isButtonLoading}
              onClick={handleConfirm}
            >
              {t("shared_modal.confirm")}
            </Button>
          }
          title={t("event_details.unsubscription_modal.title")}
        >
          <Text variant="body1">{t("event_details.unsubscription_modal.content")}</Text>
        </Modal>
      )}
    </>
  )
}
